// @fileoverview : 경남-KPI > 전력 사용량(E)

import { KPIActionHeader } from "@/features/kpi/actionHeader";
import { useKPIData } from "@/features/kpi/hook/useKPIData";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { GridTable } from "@/features/ui/PMS/gridTable";
import CustomTable from "@/features/ui/table/CustomTable";
import useColumnList from "@/hooks/useColumnList";
import { Audit } from "@/pages/KPI/leadtime";
import { KPIPageProps } from "@/pages/KPI/type";
import { DatetimeUtilMinute } from "@/utils/dateTimeUtil";
import { Flex } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const KPIPowerUsage = Object.assign(
  {},
  {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    KPIActionHeader: KPIActionHeader,
    CustomTable: CustomTable,
  },
);

export const KPIPowerUsagePage = ({ mode }: KPIPageProps) => {
  const queryClient = useQueryClient();
  const today = dayjs().subtract(1, "day").startOf("day").toDate();
  const oneWeekAgo = dayjs().subtract(7, "day").startOf("day").toDate();
  const columnlist = useColumnList();
  const { t } = useTranslation();

  const [content, setContent] = useState<any>([]);
  const [selectList, setSelectList] = useState<Set<number>>(new Set());
  const [stateDate, setStateDate] = useState<[Date | null, Date | null]>([oneWeekAgo, today]);
  const [item, setItem] = useState<string | null>("");
  const [averagePowerUsage, setAveragePowerUsage] = useState<string>("");

  const { itemData, powerUsageData, auditKPIData } = useKPIData({
    mode,
    value: item,
    stateDate,
    options: Audit ? { fetchAuditKPI: true } : { fetchPowerUsage: true },
  });

  const TableHeader = {
    machine: [
      t("item.code"),
      t("item.name"),
      t("item.spec"),
      t("kpi.powerUsage.averagePowerUsage"),
    ],
  };

  const BodyData = {
    machine: [
      {
        [t("item.code")]: itemData?.code ?? "-",
        [t("item.name")]: itemData?.name,
        [t("item.spec")]: itemData?.spec,
        [t("kpi.powerUsage.averagePowerUsage")]:
          averagePowerUsage === "NaN" ? "-" : averagePowerUsage,
      },
    ],
  };

  useEffect(() => {
    const transform = (data: any[]) =>
      data.map((d) => ({
        id: d.id,
        productionPlanId: d.productionPlanId,
        operationName: d.operationName,
        equipmentName: d.equipmentName,
        equipmentVolt: d.equipmentVolt,
        itemCode: d.itemCode,
        itemName: d.itemName,
        itemSpec: d.itemSpec,
        workerName: d.workerName,
        workStartTime: DatetimeUtilMinute(d.workStartTime),
        workEndTime: DatetimeUtilMinute(d.workEndTime),
        pauseDuration: d.pauseDuration,
        totalQuantity: `${d.totalQuantity} ${itemData?.unitText ?? ""}`,
        goodQuantity: `${d.goodQuantity} ${itemData?.unitText ?? ""}`,
        defectQuantity: `${d.defectQuantity} ${itemData?.unitText ?? ""}`,
        electricPower: d.electricPower,
      }));

    const calculateAverage = (data: any[]) => {
      if (!Array.isArray(data) || data.length === 0) return "0.00";
      const total = data.reduce((sum, d) => sum + parseFloat(d.electricPower), 0);
      return (total / data.length).toFixed(2);
    };

    if (Array.isArray(powerUsageData?.data)) {
      const transformed = transform(powerUsageData?.data ?? []);
      setContent(transformed);
      setAveragePowerUsage(calculateAverage(powerUsageData?.data ?? []));
    } else if (Array.isArray(auditKPIData?.data?.rows ?? [])) {
      const rows = auditKPIData?.data.rows;
      const transformed = transform(rows ?? []);
      setContent(transformed);
      setAveragePowerUsage(calculateAverage(rows ?? []));
    } else {
      setContent([]);
      setAveragePowerUsage("0.00");
    }
  }, [auditKPIData, itemData, powerUsageData]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["kpi"] });
    };
  }, []);

  return (
    <Flex gap="md" direction={"column"} p="sm">
      <KPIPowerUsage.Header>
        <KPIPowerUsage.HeaderTitle>{t("kpi.powerUsage.powerUsage")}(E)</KPIPowerUsage.HeaderTitle>
        <KPIPowerUsage.HeaderSubTitle>{t("kpi.powerUsage.subTitle")}</KPIPowerUsage.HeaderSubTitle>
      </KPIPowerUsage.Header>
      <KPIPowerUsage.KPIActionHeader
        setItem={setItem}
        stateDate={stateDate}
        setStateDate={setStateDate}
        mode={mode}
      />

      <KPIPowerUsage.CustomTable headers={TableHeader.machine} data={BodyData.machine} />
      <GridTable
        headerList={columnlist.powerUsageTable}
        row={content}
        setRow={setContent}
        selectList={selectList}
        //@ts-ignore
        setSelectList={setSelectList}
        width={"100%"}
        height={750}
        resizable={true}
      />
    </Flex>
  );
};
